import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { CheckCircle2, XCircle, Clock, Loader2, RotateCw } from 'lucide-react';

// React 18 StrictMode double-invokes effects in development, which
// produces two concurrent POST /verify-email calls with the same
// token — the backend then flips the second call to `already_used`
// and the user is shown the wrong modal.
//
// The dedup uses TWO layers:
//   1. `IN_FLIGHT` — a module-scope Map so the second mount, while the
//      first POST is still awaiting, reuses the SAME promise instead
//      of racing a second request.
//   2. `sessionStorage` — survives HMR / dev-server module reload and
//      accidental page refresh, so a re-mount after the response has
//      settled still serves the original result.
const IN_FLIGHT = new Map(); // token -> Promise<res>
const SS_KEY = (token) => `verify-email:${token}`;

// Public page — consumes the raw token from the URL and confirms
// verification WITHOUT creating any session. On success, the user is
// invited to click "Se connecter" and go through /login normally.
export default function VerifyEmailPage() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const { verifyEmail } = useAuth();
    const [state, setState] = useState({ kind: 'loading' });

    useEffect(() => {
        const applyResult = (res) => {
            if (res && res.success) {
                setState({ kind: res.status === 'already_used' ? 'already_used' : 'verified' });
            } else {
                setState({ kind: (res && res.code === 'expired') ? 'expired' : 'invalid' });
            }
        };

        (async () => {
            if (!token) {
                setState({ kind: 'invalid' });
                return;
            }
            // Layer 2 — settled result cache (survives HMR / refresh).
            try {
                const cached = sessionStorage.getItem(SS_KEY(token));
                if (cached) { applyResult(JSON.parse(cached)); return; }
            } catch (_) { /* sessionStorage unavailable — proceed. */ }

            // Layer 1 — dedup within a single page load. The StrictMode
            // second mount reuses this SAME promise instead of racing a
            // duplicate POST.
            let pending = IN_FLIGHT.get(token);
            if (!pending) {
                pending = (async () => {
                    const r = await verifyEmail(token);
                    try { sessionStorage.setItem(SS_KEY(token), JSON.stringify(r)); } catch (_) {}
                    return r;
                })();
                IN_FLIGHT.set(token, pending);
            }
            applyResult(await pending);
        })();
    }, [token, verifyEmail]);

    const shell = (icon, tone, title, body, primary, testid) => (
        <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-4 py-12" data-testid={`verify-email-${testid}`}>
            <div className="max-w-md w-full bg-white p-8 rounded-2xl border border-[#E5E7EB] shadow-sm text-center">
                <div className="flex justify-center mb-6">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${tone}`}>
                        {icon}
                    </div>
                </div>
                <h1 className="text-2xl font-bold text-[#0A0A0A] mb-3 font-['Outfit']">{title}</h1>
                <p className="text-[#4B5563] mb-6 whitespace-pre-line">{body}</p>
                {primary}
            </div>
        </div>
    );

    if (state.kind === 'loading') {
        return shell(
            <Loader2 className="w-8 h-8 text-[#002FA7] animate-spin" />,
            'bg-[#EEF2FF]',
            t('auth.verify_email.loading_title'),
            t('auth.verify_email.loading_body'),
            null,
            'loading',
        );
    }

    if (state.kind === 'verified' || state.kind === 'already_used') {
        return shell(
            <CheckCircle2 className="w-9 h-9 text-[#059669]" />,
            'bg-[#D1FAE5]',
            state.kind === 'verified' ? t('auth.verify_email.verified_title') : t('auth.verify_email.already_title'),
            state.kind === 'verified' ? t('auth.verify_email.verified_body') : t('auth.verify_email.already_body'),
            <Link to="/login" data-testid="verify-email-login-btn">
                <Button className="bg-[#002FA7] hover:bg-[#001F70] w-full">{t('auth.verify_email.sign_in')}</Button>
            </Link>,
            state.kind,
        );
    }

    if (state.kind === 'expired') {
        return shell(
            <Clock className="w-9 h-9 text-[#D97706]" />,
            'bg-[#FEF3C7]',
            t('auth.verify_email.expired_title'),
            t('auth.verify_email.expired_body'),
            <Link to="/verify-email-required" data-testid="verify-email-resend-btn">
                <Button variant="outline" className="w-full">
                    <RotateCw className="w-4 h-4 mr-2" /> {t('auth.verify_email.resend_link')}
                </Button>
            </Link>,
            'expired',
        );
    }

    // invalid / unknown
    return shell(
        <XCircle className="w-9 h-9 text-[#DC2626]" />,
        'bg-[#FEE2E2]',
        t('auth.verify_email.invalid_title'),
        t('auth.verify_email.invalid_body'),
        <Link to="/verify-email-required" data-testid="verify-email-resend-btn">
            <Button variant="outline" className="w-full">
                <RotateCw className="w-4 h-4 mr-2" /> {t('auth.verify_email.resend_link')}
            </Button>
        </Link>,
        'invalid',
    );
}
